import { ImagesBlogBlogThumbnailJpeg, ImagesBlogClockSvg } from '@/assets/images_blog.assets';
import { BlogPost } from '@/types/Blog';
import Image from '@/components/common/SpiritoryImage';
import DefaultLink from '../common/default/DefaultLink';
import { cn } from '@/lib/utils';
import { ImagesPressLogosGoldBlueSpiritoryLogoJpg } from '@/assets/images_press_logos_gold-blue.assets';
import LoadingComponent from '../common/LoadingComponent';
import LoadingImage from '../common/LoadingImage';
import { buildStrapiImageUrl } from '@/utilites/strapi.utils';

export default function BlogPageCard({
  blog,
  mainCard = false,
}: {
  blog: BlogPost | null;
  mainCard?: boolean;
}) {
  if (blog === null) {
    return (
      <LoadingComponent
        as="div"
        loading={true}
        componentProps={{
          className: cn({
            'tw-w-full': mainCard,
          }),
        }}
      >
        <article>
          <div className="tw-relative">
            <LoadingImage
              className={cn('max-nine:tw-h-40 max-nine:tw-w-full', {
                ['tw-h-[495px] tw-w-[1325px]']: mainCard,
                ['tw-h-[196px] tw-w-[427px]']: !mainCard,
              })}
              loading={true}
            >
              <></>
            </LoadingImage>
            <span
              className={cn(
                'tw !tw-absolute tw-bottom-5 tw-right-5 tw-z-10 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-rounded-md tw-bg-white tw-bg-opacity-70 tw-p-2 tw-text-sm tw-font-medium tw-text-[#15253FCC]'
              )}
            >
              <Image src={ImagesBlogClockSvg} alt="Clock" width={16} height={16} />
              07/11/2024
            </span>
          </div>
          <div className="tw-flex tw-w-full tw-flex-col tw-pt-6">
            <div className="tw-flex tw-flex-col tw-gap-4">
              <span className="tw-mb-0 tw-text-left tw-text-sm tw-font-normal tw-text-text">
                Traditions
              </span>
              <div className="tw-flex tw-flex-col tw-gap-1">
                <div>
                  <h3
                    className={cn(
                      'tw-line-clamp-2 tw-min-h-[2lh] tw-text-left tw-font-semibold tw-text-text tw-underline tw-underline-offset-4',
                      {
                        ['tw-text-2xl']: mainCard,
                        ['tw-text-lg']: !mainCard,
                      }
                    )}
                  >
                    Lorem ipsum dolor sit, amet consectetur adipisicing elit. Tenetur, amet.
                  </h3>
                </div>
                <p
                  className={cn(
                    'tw-line-clamp-2 tw-text-left tw-text-[15px] tw-font-medium tw-text-[#4F5A67]'
                  )}
                >
                  Lorem ipsum dolor sit amet consectetur adipisicing elit. Enim non qui autem
                  quisquam! Minima iste asperiores, saepe vitae aperiam natus explicabo magnam
                  beatae voluptas suscipit ratione eum nulla ea porro magni! Aspernatur, commodi
                  aliquam? Laboriosam aperiam sed beatae sequi perferendis.
                </p>
              </div>
            </div>

            <div className="tw-flex tw-w-full tw-items-center tw-justify-between tw-pb-2">
              <span className="tw-font-plusJakartaSans tw-text-sm tw-font-medium tw-text-[#15253F80]">
                07/11/2024
              </span>
              <div className="tw-flex tw-items-center tw-gap-2">
                <span className="tw-text-sm tw-font-semibold tw-text-text">Spiritory Team</span>
                <div className="tw-rounded-full tw-bg-white">
                  <Image
                    src={ImagesPressLogosGoldBlueSpiritoryLogoJpg}
                    alt="Spiritory Logo"
                    width={42}
                    height={42}
                    className="tw-h-full tw-w-full tw-object-contain"
                  />
                </div>
              </div>
            </div>
          </div>
        </article>
      </LoadingComponent>
    );
  }

  const blogTitle = blog.title;
  const blogDescription = blog.description;
  const blogThumbnailImage = (blog as any).thumbnailImage;
  const blogCreated = (blog as any).created;
  const blogReadingTime = (blog as any).reading_time;
  const blogSlug = (blog as any).slug;
  const blogLocale = (blog as any).locale;
  const blogContributor = (blog as any).contributor;
  const blogCategory = (blog as any).category;

  const categoryName = blogCategory?.name;
  const contributorName = blogContributor?.name;
  const contributorLongDescription = blogContributor?.long_description;
  const contributorSlug = blogContributor?.slug;

  const blogThumbnailImageUrl =
    buildStrapiImageUrl(
      blogThumbnailImage?.url ||
        (typeof blogThumbnailImage === 'string' ? blogThumbnailImage : '')
    ) || '';

  const blogThumbnailImageAlt = blogThumbnailImage?.alternativeText || 'Blog Thumbnail';

  const contributorImageUrl =
    buildStrapiImageUrl(blogContributor?.image?.url) || ImagesPressLogosGoldBlueSpiritoryLogoJpg.src;
  return (
    <div
      className={cn({
        'tw-w-full': mainCard,
      })}
    >
      <article
        className={`max-sm:tw-rounded-xl max-sm:tw-border max-sm:!tw-border-solid max-sm:tw-border-gray-200 max-sm:tw-shadow`}
      >
        <DefaultLink
          className="tw-relative tw-w-full"
          href={cn(`/news/${blogSlug}`)}
          locale={blogLocale}
        >
          <Image
            src={blogThumbnailImageUrl}
            alt={blogThumbnailImageAlt}
            width={mainCard ? 1325 : 427}
            height={mainCard ? 495 : 196}
            className="tw-w-full tw-rounded-xl tw-object-cover max-nine:tw-h-40 max-sm:tw-rounded-b-none"
            priority={mainCard}
            fetchPriority={mainCard ? 'high' : undefined}
          />
          {blogReadingTime && (
            <span className="tw tw-absolute tw-bottom-5 tw-right-5 tw-z-10 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-rounded-md tw-bg-white tw-bg-opacity-70 tw-p-2 tw-text-sm tw-font-medium tw-text-[#15253FCC] max-nine:tw-text-xs">
              <Image src={ImagesBlogClockSvg} alt="Clock" width={16} height={16} />
              {blogReadingTime} min
            </span>
          )}
        </DefaultLink>
        <div className="tw-flex tw-w-full tw-flex-col tw-pt-6 max-sm:tw-p-[15px]">
          <div className="tw-flex tw-flex-col tw-gap-4">
            <span className="tw-mb-0 tw-text-left tw-text-sm tw-font-normal tw-text-text">
              {categoryName}
            </span>
            <div className="tw-flex tw-flex-col tw-gap-1">
              <DefaultLink href={cn(`/news/${blogSlug}`)} locale={blogLocale}>
                <h3
                  className={cn(
                    'tw-line-clamp-2 tw-min-h-[2lh] tw-text-left tw-font-semibold tw-text-text tw-underline-offset-4 max-nine:tw-text-sm',
                    {
                      ['tw-text-2xl']: mainCard,
                      ['tw-text-lg']: !mainCard,
                    }
                  )}
                >
                  {blogTitle}
                </h3>
              </DefaultLink>
              <p
                className={cn(
                  'tw-line-clamp-2 tw-text-left tw-text-[15px] tw-font-normal tw-text-black max-nine:tw-text-xs'
                )}
              >
                {blogDescription}
              </p>
            </div>
          </div>

          <div className="tw-flex tw-w-full tw-items-center tw-justify-between tw-pb-2">
            <span className="tw-font-plusJakartaSans tw-text-sm tw-font-medium tw-text-[#00020680]">
              {blogCreated ? new Date(blogCreated).toLocaleDateString() : ''}
            </span>
            <DefaultLink
              href={cn(contributorLongDescription ? `/authors/${contributorSlug}` : '#')}
              className="tw-flex tw-items-center tw-gap-2"
            >
              <span className="tw-text-sm tw-font-bold tw-text-text tw-underline">
                {contributorName}
              </span>
              <div className="tw-rounded-full tw-bg-white">
                <Image
                  src={contributorImageUrl}
                  alt={contributorName || ''}
                  width={40}
                  height={40}
                  className="tw-aspect-square tw-h-full tw-max-h-[40px] tw-w-full tw-max-w-[40px] tw-flex-shrink-0 tw-rounded-[20px] tw-object-cover"
                />
              </div>
            </DefaultLink>
          </div>
        </div>
      </article>
    </div>
  );
}
